import React, { Dispatch, SetStateAction } from 'react';

import { useTranslation } from '@crehana/i18n';
import { Skeleton } from '@crehana/ui';
import { getImgxUrl } from '@crehana/utils';

import QuickEnroll from 'Jsx/CheckoutV4/components/QuickEnroll';
import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';
import {
  DegreeQuota,
  PaymentMethods,
  PaymentMethodType,
} from 'Jsx/CheckoutV4/types';
import { getStepsForMethod, isCardPaymentMethod } from 'Jsx/CheckoutV4/utils';

import PaymentMethod from './PaymentMethod';
import PaymentMethodsTab from './PaymentMethodsTab';

type PaymentMethodsSectionProps = {
  paymentMethods: PaymentMethods;
  isQuickEnrollEnabled: boolean;
  isGuestCheckout: boolean;
  isMarketProduct: boolean;
  symbol: string | null;
  quotas?: DegreeQuota[];
  selectedQuotaNumber: number;
  setSelectedQuotaNumber: Dispatch<SetStateAction<number>>;
  isDesktop?: boolean;
};

const PaymentMethodsSection = ({
  paymentMethods,
  isMarketProduct,
  isQuickEnrollEnabled,
  isGuestCheckout,
  symbol,
  quotas,
  selectedQuotaNumber,
  setSelectedQuotaNumber,
  isDesktop,
}: PaymentMethodsSectionProps) => {
  const {
    isFetchingPaymentMethods,
    setSelectedPaymentMethod,
    selectedPaymentMethod,
    setStepsToBuy,
  } = useCheckoutContext();

  const { t } = useTranslation();

  if (isFetchingPaymentMethods) {
    return (
      <div>
        <div className="flex flex-row flex-wrap">
          <div className="w-1/2 border-4 border-transparent border-solid h-72 max-w-200">
            <Skeleton className="w-full h-full rounded-5" />
          </div>
          <div className="w-1/2 border-4 border-transparent border-solid h-72 max-w-200">
            <Skeleton className="w-full h-full rounded-5" />
          </div>
          <div className="w-1/2 border-4 border-transparent border-solid h-72 max-w-200">
            <Skeleton className="w-full h-full rounded-5" />
          </div>
        </div>

        <div className="max-w-360">
          <Skeleton className="w-2/3 h-20 mt-24 mb-8 rounded-5" />
          <Skeleton className="w-full h-56 mb-24 rounded-5" />

          <Skeleton className="w-2/3 h-20 mt-24 mb-8 rounded-5" />
          <Skeleton className="w-full h-56 mb-24 rounded-5" />

          <Skeleton className="w-2/3 h-20 mt-24 mb-8 rounded-5" />
          <Skeleton className="w-full h-56 rounded-5" />
        </div>
      </div>
    );
  }

  const selectAnotherPaymentMethod = (paymentMethod: PaymentMethodType) => {
    setSelectedPaymentMethod(paymentMethod);

    setStepsToBuy(
      getStepsForMethod(paymentMethod?.title, isMarketProduct, isGuestCheckout),
    );
  };

  if (isQuickEnrollEnabled) {
    return (
      <div className="hidden md:block">
        <QuickEnroll isGuestCheckout={isGuestCheckout} />
      </div>
    );
  }

  const logoUrl = selectedPaymentMethod?.logoUrl;

  return (
    <div>
      {!selectedPaymentMethod && (
        <>
          <h2 className="font-h2 mb-24 text-center md:hidden">
            {t('CHOOSE_PAYMENT_METHOD')}
          </h2>
          <h2 className="font-h3 mb-24  hidden md:block">
            {t('CHOOSE_PAYMENT_METHOD')}
          </h2>
          <PaymentMethodsTab
            paymentMethods={paymentMethods}
            selectedPaymentMethod={selectedPaymentMethod}
            selectAnotherPaymentMethod={selectAnotherPaymentMethod}
          />
        </>
      )}
      {selectedPaymentMethod && (
        <div>
          {!isCardPaymentMethod(selectedPaymentMethod?.title as string) && (
            <p className="mb-24">
              {logoUrl && (
                <img
                  src={getImgxUrl(logoUrl, { h: 24, dpr: 2, auto: 'format' })}
                  alt={
                    selectedPaymentMethod?.offlineLabel ||
                    selectedPaymentMethod?.title
                  }
                  className="block h-24 sm:mr-8"
                />
              )}
            </p>
          )}
          <PaymentMethod
            symbol={symbol}
            quotas={quotas}
            selectedQuotaNumber={selectedQuotaNumber}
            setSelectedQuotaNumber={setSelectedQuotaNumber}
            selectedPaymentMethod={selectedPaymentMethod}
            isDesktop={isDesktop}
          />
        </div>
      )}
    </div>
  );
};

export default PaymentMethodsSection;
